import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Copy, Clock, FileText } from "lucide-react";

const STATUS = {
  awaiting_payment: { label: "Awaiting payment", cls: "text-amber-300", icon: <Clock size={18} /> },
  payment_submitted: { label: "Payment submitted — under review", cls: "text-cyan-300", icon: <Clock size={18} /> },
  paid: { label: "Payment confirmed", cls: "text-emerald-400", icon: <CheckCircle2 size={18} /> },
  fulfilled: { label: "Fulfilled", cls: "text-emerald-400", icon: <CheckCircle2 size={18} /> },
  cancelled: { label: "Cancelled", cls: "text-rose-400", icon: <Clock size={18} /> },
};

export default function InvoicePage() {
  const { num } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/invoice/${num}`).then(({ data }) => { setInv(data); if (!method) setMethod(data.payment_method || data.payment_methods?.[0]?.key || ""); }).catch(() => setInv(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [num]);

  if (inv === false) return <div className="pt-32 pb-24 text-center text-slate-400">Invoice not found. <Link to="/shop" className="text-cyan-300 underline">Continue shopping</Link></div>;
  if (!inv) return <div className="pt-32 pb-24 text-center text-cyan-300 font-mono animate-pulse">Loading invoice…</div>;

  const st = STATUS[inv.status] || STATUS.awaiting_payment;
  const selected = (inv.payment_methods || []).find((m) => m.key === method);
  const paid = ["paid", "fulfilled"].includes(inv.status);

  const submit = async () => {
    if (!method) return toast.error("Select how you paid");
    setSubmitting(true);
    try {
      await api.post(`/invoice/${num}/submit`, { payment_method: method, reference });
      toast.success("Thank you — we'll confirm your payment shortly.");
      load();
    } catch { toast.error("Could not submit. Please try again."); }
    finally { setSubmitting(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-4xl mx-auto" data-testid="invoice-page">
      <div className="mono-label mb-3 flex items-center gap-2"><FileText size={14} /> Invoice {inv.invoice_number}</div>
      <h1 className="font-serif text-4xl sm:text-5xl text-white mb-2">Complete your payment</h1>
      <div className={`flex items-center gap-2 text-sm font-mono mb-8 ${st.cls}`} data-testid="invoice-status">{st.icon} {st.label}</div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* summary */}
        <div className="glass-strong rounded-2xl p-6 space-y-3 h-fit">
          <div className="mono-label mb-2">Order summary</div>
          {inv.items.map((x, i) => (
            <div key={i} className="flex justify-between text-sm font-mono border-b border-white/5 pb-2">
              <span className="text-slate-300">{x.name} × {x.qty}</span>
              <span>${x.line.toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span className="font-mono">${inv.subtotal.toFixed(2)}</span></div>
          {inv.bulk_pct > 0 && <div className="flex justify-between text-sm text-emerald-400"><span>Bulk discount ({inv.bulk_pct}%)</span><span className="font-mono">−${inv.bulk_discount.toFixed(2)}</span></div>}
          <div className="laser-line" />
          <div className="flex justify-between text-lg"><span>Total due</span><span className="font-mono text-cyan-300" data-testid="invoice-total">${inv.total.toFixed(2)}</span></div>
          <p className="text-[11px] text-slate-500 mt-2">Email: {inv.email}</p>
        </div>

        {/* payment */}
        <div className="space-y-4">
          {paid ? (
            <div className="glass rounded-2xl p-6 text-center">
              <CheckCircle2 className="text-emerald-400 mx-auto mb-3" size={40} />
              <div className="text-white font-semibold">Payment confirmed</div>
              <p className="text-sm text-slate-400 mt-1">Thank you! Your order is being prepared. Keep your invoice number <b className="text-slate-200">{inv.invoice_number}</b> for your records.</p>
              <Link to="/my-lab" className="inline-block mt-5 px-6 py-2.5 rounded-full btn-primary text-sm">View my orders</Link>
            </div>
          ) : (
            <>
              <div className="glass rounded-2xl p-6">
                <div className="mono-label mb-3">Pay with</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(inv.payment_methods || []).map((m) => (
                    <button key={m.key} onClick={() => setMethod(m.key)} data-testid={`invoice-method-${m.key}`}
                      className={`px-3 py-1.5 rounded-lg text-sm border transition ${method === m.key ? "bg-cyan-400 text-black border-cyan-400" : "border-white/15 text-slate-200 hover:border-cyan-400/50"}`}>{m.label}</button>
                  ))}
                </div>
                {selected && (
                  <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="invoice-instructions">
                    <p className="text-sm text-slate-300 leading-relaxed">{selected.instructions}</p>
                    {selected.handle ? (
                      <div className="mt-3 flex items-center gap-2">
                        <code className="flex-1 text-cyan-300 bg-black/50 rounded-lg px-3 py-2 text-sm break-all">{selected.handle}</code>
                        <button onClick={() => copy(selected.handle)} className="p-2 text-slate-300 hover:text-cyan-300"><Copy size={16} /></button>
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-amber-300/80 font-mono">Payment details will appear here shortly — or contact us to receive them.</p>
                    )}
                    <div className="mt-3 text-xs text-slate-400">Reference your invoice <b className="text-white">{inv.invoice_number}</b> and pay <b className="text-white">${inv.total.toFixed(2)}</b>.</div>
                  </div>
                )}
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="mono-label mb-2">Already paid?</div>
                <p className="text-xs text-slate-400 mb-3">Enter your transaction ID / confirmation so we can verify and release your order faster.</p>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Transaction ID / confirmation (optional)"
                  data-testid="invoice-reference" className="bg-black/40 border-white/10 mb-3" />
                <Button onClick={submit} disabled={submitting} data-testid="invoice-submit-payment" className="w-full btn-primary rounded-xl py-3 text-sm">
                  {submitting ? "Submitting…" : "I've paid — notify CG Labz"}
                </Button>
              </div>
            </>
          )}
          <button onClick={() => navigate("/shop")} className="w-full text-xs font-mono text-slate-500 hover:text-slate-300">Continue shopping</button>
        </div>
      </div>
      <p className="text-[10px] font-mono text-slate-600 text-center mt-10">For laboratory research only. Not for human consumption.</p>
    </div>
  );
}
